import { useState } from "react";
import { X, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * BulkJobEditDialog — apply the same PATCH payload to N selected jobs.
 *
 * Only fields the user actually TOUCHES get sent — the "changed" flag on each
 * field controls whether it goes in the payload. This prevents accidental
 * blanking of status/priority when the user only wanted to bulk-set a due date.
 *
 * Uses the atomic ``POST /api/jobs/bulk-update`` endpoint which returns a
 * 30-second snapshot id. On success we surface an Undo toast that calls
 * ``POST /api/jobs/bulk-update/undo/{snapshot_id}`` to restore previous
 * values.
 */
export default function BulkJobEditDialog({ jobIds, onClose, onSaved }) {
    const [changes, setChanges] = useState({
        // {value, changed:bool} per field. Only "changed=true" fields are sent.
        status: { value: "", changed: false },
        priority: { value: "", changed: false },
        work_mode: { value: "", changed: false },
        urgency: { value: "", changed: false },
        due_date: { value: "", changed: false },
        hire_start_date: { value: "", changed: false },
        openings: { value: "", changed: false },
        // B11 — highlight for embed board spotlight.
        highlight: { value: "true", changed: false },
        highlight_color: { value: "#FBBF24", changed: false },
        // B12 — auto-expiring spotlight.
        spotlight_until: { value: "", changed: false },
    });
    const [saving, setSaving] = useState(false);

    const setField = (key, value) => setChanges((cur) => ({
        ...cur, [key]: { value, changed: true },
    }));
    const unsetField = (key) => setChanges((cur) => ({
        ...cur, [key]: { value: "", changed: false },
    }));

    const payload = () => {
        const out = {};
        for (const [k, v] of Object.entries(changes)) {
            if (!v.changed) continue;
            if (k === "openings") {
                const n = parseInt(v.value, 10);
                if (Number.isFinite(n) && n > 0) out[k] = n;
            } else if (k === "highlight") {
                out[k] = v.value === "true";
            } else if (k === "spotlight_until") {
                // datetime-local returns "YYYY-MM-DDTHH:MM"; append seconds + Z to satisfy ISO-8601.
                if (v.value) {
                    const iso = v.value.length === 16 ? `${v.value}:00Z` : v.value;
                    out[k] = iso;
                }
            } else if (v.value !== "") {
                out[k] = v.value;
            }
        }
        return out;
    };

    const submit = async () => {
        const body = payload();
        const changedFields = Object.keys(body);
        if (changedFields.length === 0) {
            toast.error("Toggle at least one field to apply changes");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post("/jobs/bulk-update", {
                job_ids: jobIds,
                updates: body,
            });
            const updated = data?.updated || 0;
            const failed = data?.failed || 0;
            const snapshotId = data?.snapshot_id;
            const ttl = data?.undo_ttl_seconds || 30;

            if (updated > 0 && snapshotId) {
                // Undo toast — visible for the full TTL window (minus a small
                // buffer so the user still has time to click after seeing it).
                toast.success(
                    `Updated ${updated} job${updated === 1 ? "" : "s"} (${changedFields.join(", ")})`,
                    {
                        duration: Math.max(4000, (ttl - 2) * 1000),
                        action: {
                            label: "Undo",
                            onClick: async () => {
                                try {
                                    const res = await api.post(`/jobs/bulk-update/undo/${snapshotId}`);
                                    toast.success(`Restored ${res?.data?.restored || 0} jobs`);
                                    onSaved?.();
                                } catch (err) {
                                    toast.error(formatApiError(err));
                                }
                            },
                        },
                    }
                );
            }
            if (failed > 0) {
                toast.warning(`${failed} job${failed === 1 ? "" : "s"} could not be updated`);
            }
            if (updated === 0 && failed === 0) {
                toast.info("Nothing changed");
            }
            onSaved?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
            data-testid="jobs-bulk-edit-modal"
        >
            <div className="bg-white border border-border w-full max-w-lg max-h-[85vh] flex flex-col">
                <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                    <span className="text-[11px] font-mono uppercase tracking-widest text-ikb">Bulk edit · {jobIds.length} job{jobIds.length === 1 ? "" : "s"}</span>
                    <button type="button" onClick={onClose} disabled={saving} className="ml-auto text-foreground/60 hover:text-foreground disabled:opacity-40" aria-label="Close" data-testid="jobs-bulk-edit-close">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-4 space-y-3 overflow-y-auto text-sm">
                    <p className="text-[11px] text-foreground/70 leading-relaxed">
                        Only the fields you toggle will be updated. Untouched fields remain intact on each job.
                    </p>
                    <BulkField label="Status" k="status" changes={changes} setField={setField} unsetField={unsetField}
                        options={["draft", "active", "paused", "closed"]} testId="bulk-field-status" />
                    <BulkField label="Priority" k="priority" changes={changes} setField={setField} unsetField={unsetField}
                        options={["low", "normal", "high", "urgent"]} testId="bulk-field-priority" />
                    <BulkField label="Work mode" k="work_mode" changes={changes} setField={setField} unsetField={unsetField}
                        options={["remote", "hybrid", "onsite"]} testId="bulk-field-work-mode" />
                    <BulkField label="Urgency" k="urgency" changes={changes} setField={setField} unsetField={unsetField}
                        options={["low", "medium", "high", "critical"]} testId="bulk-field-urgency" />
                    <BulkField label="Due date" k="due_date" changes={changes} setField={setField} unsetField={unsetField}
                        type="date" testId="bulk-field-due-date" />
                    <BulkField label="Hire start date" k="hire_start_date" changes={changes} setField={setField} unsetField={unsetField}
                        type="date" testId="bulk-field-hire-start" />
                    <BulkField label="Openings" k="openings" changes={changes} setField={setField} unsetField={unsetField}
                        type="number" testId="bulk-field-openings" />
                    <BulkField label="Highlight (spotlight on embed board)" k="highlight" changes={changes} setField={setField} unsetField={unsetField}
                        options={["true", "false"]} labels={{"true":"Yes — spotlight","false":"No — remove spotlight"}} testId="bulk-field-highlight" />
                    <BulkField label="Highlight color (hex)" k="highlight_color" changes={changes} setField={setField} unsetField={unsetField}
                        type="color" testId="bulk-field-highlight-color" />
                    <BulkField label="Spotlight until (auto-expires highlight)" k="spotlight_until" changes={changes} setField={setField} unsetField={unsetField}
                        type="datetime-local" testId="bulk-field-spotlight-until" />
                </div>
                {saving && (
                    <div className="px-4 py-2 border-t border-border bg-secondary/30 text-[11px] font-mono flex items-center gap-2">
                        <Loader2 className="w-3.5 h-3.5 animate-spin text-ikb" />
                        Applying to {jobIds.length} job{jobIds.length === 1 ? "" : "s"}…
                    </div>
                )}
                <div className="px-4 py-3 border-t border-border flex items-center gap-2">
                    <button type="button" onClick={onClose} disabled={saving} className="h-9 px-3 border border-border text-[11px] font-mono uppercase tracking-widest hover:border-ikb hover:text-ikb disabled:opacity-40" data-testid="jobs-bulk-edit-cancel">
                        Cancel
                    </button>
                    <button type="button" onClick={submit} disabled={saving} className="ml-auto h-9 px-3 bg-ikb text-white text-[11px] font-mono uppercase tracking-widest hover:bg-[#001f7a] disabled:opacity-50 disabled:cursor-not-allowed" data-testid="jobs-bulk-edit-submit">
                        {saving ? "Applying…" : `Apply to ${jobIds.length} job${jobIds.length === 1 ? "" : "s"}`}
                    </button>
                </div>
            </div>
        </div>
    );
}


function BulkField({ label, k, changes, setField, unsetField, options, labels, type, testId }) {
    const state = changes[k];
    const active = state.changed;
    return (
        <div className={`border p-2 ${active ? "border-ikb bg-ikb/5" : "border-border"}`} data-testid={testId}>
            <label className="flex items-center gap-2 cursor-pointer">
                <input
                    type="checkbox"
                    checked={active}
                    onChange={(e) => (e.target.checked ? setField(k, state.value) : unsetField(k))}
                    className="accent-ikb"
                    data-testid={`${testId}-toggle`}
                />
                <span className="text-[11px] font-mono uppercase tracking-widest">{label}</span>
            </label>
            {active && (
                <div className="mt-2 pl-6">
                    {options ? (
                        <select
                            value={state.value}
                            onChange={(e) => setField(k, e.target.value)}
                            className="w-full h-8 px-2 border border-border text-sm"
                            data-testid={`${testId}-select`}
                        >
                            <option value="">— pick —</option>
                            {options.map((o) => (
                                <option key={o} value={o} className="capitalize">
                                    {labels?.[o] ?? o}
                                </option>
                            ))}
                        </select>
                    ) : (
                        <input
                            type={type || "text"}
                            value={state.value}
                            onChange={(e) => setField(k, e.target.value)}
                            className="w-full h-8 px-2 border border-border text-sm"
                            data-testid={`${testId}-input`}
                            min={type === "number" ? 1 : undefined}
                        />
                    )}
                </div>
            )}
        </div>
    );
}
